import { logger } from '@shared/lib/logger';
import {
  normalizeTranslationLocale,
  normalizeTranslationSourceLocale,
  shouldSkipTranslation,
  type TranslationLocale,
  type TranslationSourceLocale,
} from './language';

export interface TranslationCacheRecord {
  translatedText: string;
  sourceLang: TranslationSourceLocale;
  targetLang: TranslationLocale;
  cachedAt: number;
}

const CACHE_STORAGE_KEY = 'jema-translation-cache-v1';
const CACHE_MAX_ENTRIES = 2000;

const memoryCache = new Map<string, TranslationCacheRecord>();
const inFlight = new Map<string, Promise<string>>();

let storageLoaded = false;

const hashKey = (input: string): string => {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(16);
};

const buildCacheKey = (text: string, sourceLang: TranslationSourceLocale, targetLang: TranslationLocale): string =>
  hashKey(`${sourceLang}|${targetLang}|${text}`);

const ensureStorageLoaded = () => {
  if (storageLoaded || typeof window === 'undefined') return;
  storageLoaded = true;

  try {
    const raw = window.localStorage.getItem(CACHE_STORAGE_KEY);
    if (!raw) return;

    const parsed = JSON.parse(raw) as Record<string, TranslationCacheRecord>;
    Object.entries(parsed).forEach(([key, value]) => {
      if (!value || typeof value.translatedText !== 'string') return;
      memoryCache.set(key, value);
    });
  } catch (error) {
    logger.warn('Failed to load translation cache from localStorage', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

const persistCache = () => {
  if (typeof window === 'undefined') return;

  try {
    const entries = Array.from(memoryCache.entries());
    const trimmedEntries = entries.slice(Math.max(0, entries.length - CACHE_MAX_ENTRIES));
    const payload = Object.fromEntries(trimmedEntries);
    window.localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    logger.warn('Failed to persist translation cache to localStorage', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

const setCacheValue = (key: string, value: TranslationCacheRecord) => {
  memoryCache.set(key, value);
  if (memoryCache.size > CACHE_MAX_ENTRIES) {
    const overflow = memoryCache.size - CACHE_MAX_ENTRIES;
    const keysToDelete = Array.from(memoryCache.keys()).slice(0, overflow);
    keysToDelete.forEach((cacheKey) => memoryCache.delete(cacheKey));
  }
  persistCache();
};

const parseTranslatePayload = (payload: unknown): string | null => {
  if (!Array.isArray(payload)) return null;
  const chunks = payload[0];
  if (!Array.isArray(chunks)) return null;

  const translated = chunks
    .map((chunk) => (Array.isArray(chunk) && typeof chunk[0] === 'string' ? chunk[0] : ''))
    .join('')
    .trim();

  return translated || null;
};

const fetchTranslatedText = async (
  text: string,
  sourceLang: TranslationSourceLocale,
  targetLang: TranslationLocale,
): Promise<string> => {
  const params = new URLSearchParams({
    client: 'gtx',
    sl: sourceLang,
    tl: targetLang,
    dt: 't',
    q: text,
  });

  const response = await fetch(`https://translate.googleapis.com/translate_a/single?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`Translation request failed with status ${response.status}`);
  }

  const payload = (await response.json()) as unknown;
  const translated = parseTranslatePayload(payload);
  if (!translated) {
    throw new Error('Unexpected translation response payload');
  }

  return translated;
};

export const translate = async (
  text: string,
  targetLang: string,
  sourceLang?: string,
): Promise<string> => {
  const original = text ?? '';
  if (!original.trim()) return original;

  ensureStorageLoaded();

  const normalizedTarget = normalizeTranslationLocale(targetLang);
  const normalizedSource = normalizeTranslationSourceLocale(sourceLang);

  if (shouldSkipTranslation(normalizedSource, normalizedTarget)) {
    return original;
  }

  const cacheKey = buildCacheKey(original, normalizedSource, normalizedTarget);
  const cached = memoryCache.get(cacheKey);
  if (cached?.translatedText) {
    return cached.translatedText;
  }

  const pending = inFlight.get(cacheKey);
  if (pending) {
    return pending;
  }

  const promise = (async () => {
    try {
      const translated = await fetchTranslatedText(original, normalizedSource, normalizedTarget);
      setCacheValue(cacheKey, {
        translatedText: translated,
        sourceLang: normalizedSource,
        targetLang: normalizedTarget,
        cachedAt: Date.now(),
      });
      return translated;
    } catch (error) {
      logger.warn('Translation request failed; returning original text', {
        error: error instanceof Error ? error.message : String(error),
        sourceLang: normalizedSource,
        targetLang: normalizedTarget,
      });
      return original;
    } finally {
      inFlight.delete(cacheKey);
    }
  })();

  inFlight.set(cacheKey, promise);
  return promise;
};

export const translateBatch = async (
  texts: string[],
  targetLang: string,
  sourceLang?: string,
): Promise<string[]> => {
  if (!texts.length) return [];

  const indexed = texts.map((text, index) => ({ text, index }));
  const uniqueMap = new Map<string, number[]>();

  indexed.forEach(({ text, index }) => {
    const key = text ?? '';
    const list = uniqueMap.get(key) ?? [];
    list.push(index);
    uniqueMap.set(key, list);
  });

  const uniqueTexts = Array.from(uniqueMap.keys());
  const translatedUnique = await Promise.all(uniqueTexts.map((value) => translate(value, targetLang, sourceLang)));

  const result = Array<string>(texts.length);
  uniqueTexts.forEach((text, uniqueIndex) => {
    const indexes = uniqueMap.get(text) ?? [];
    indexes.forEach((index) => {
      result[index] = translatedUnique[uniqueIndex] ?? texts[index];
    });
  });

  return result;
};

export const __clearTranslationCacheForTests = () => {
  memoryCache.clear();
  inFlight.clear();
  storageLoaded = false;
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(CACHE_STORAGE_KEY);
  }
};
